import React from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Button from "../components/Button";
import DatePicker from "../components/DatePicker";
import useAnalyticsDashboard from '../hooks/logics/dashboard/use-analytics-dashboard';

export type ActiveUsersApiResponse = {
  dimensions: string[];
  metrics: string[];
}[];

type AnalyticsType = {
  dimensions: string;
  metrics: number;
};

const AnalyticsCard = ({
  data,
  endDateValue,
  onEndDateChange,
  onStartDateChange,
  startDateValue,
  handleFilter,
  isLoading,
  heading,
}: {
  startDateValue: string;
  endDateValue: string;
  onStartDateChange: (e: Date | null | undefined) => void;
  onEndDateChange: (e: Date | null | undefined) => void;
  data: AnalyticsType[];
  handleFilter: () => void;
  isLoading: boolean;
  heading: string;
}) => {
  const isMobile = window.innerWidth <= 768;

  return (
    <div className="w-full mb-4 bg-white p-4 rounded-xl">
      <div className="flex lg:flex-row flex-col gap-4 items-center justify-between">
        <h1 className="text-2xl font-bold">{heading}</h1>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-start gap-2 flex-col md:flex-row md:items-center">
              <label htmlFor="startDate">From:</label>
              <DatePicker
                selected={new Date(startDateValue)}
                onChange={onStartDateChange}
                placeholderText="From"
                popperClassName="!left-7"
                maxDate={new Date(endDateValue)}
              />
            </div>
            <div className="flex items-start gap-2 flex-col md:flex-row md:items-center">
              <label htmlFor="endDate">To:</label>
              <DatePicker
                selected={new Date(endDateValue)}
                onChange={onEndDateChange}
                placeholderText="To"
                popperClassName="!-left-7"
                maxDate={new Date()}
                minDate={new Date(startDateValue)}
              />
            </div>
          </div>
          <div className="w-full md:w-auto">
            <Button
              className="w-full md:w-32 min-h-0 h-10 md:h-10"
              onClick={handleFilter}
            >
              Filter
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="w-full flex items-center justify-center py-10">
          <div className="loader"></div>
        </div>
      ) : (
        <div className="mt-10">
          <ResponsiveContainer debounce={300} width="100%" height={300}>
            <LineChart
              data={data}
              margin={{
                top: 5,
                right: isMobile ? 10 : 0,
                left: isMobile ? -40 : 0,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                padding={{ right: 40 }}
                interval="preserveEnd"
                dataKey="dimensions"
                //dateformat to be dd/MM/yyyy
                tick={{ fontSize: 10 }}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="metrics" stroke="#82ca9d" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};


const Analytics = () => {

  const {
    activeUsers,
    activeUsersLoading,
    activeUsersStart,
    activeUsersEnd,
    handleActiveUsersStart,
    handleActiveUsersEnd,
    handleActiveUsersFilter,
    firstOpen,
    firstOpenLoading,
    firstOpenStart,
    firstOpenEnd,
    handleFirstOpenStart,
    handleFirstOpenEnd,
    handleFirstOpenFilter,
  } = useAnalyticsDashboard();
  return (
    <div className="space-y-10 py-4">

      <AnalyticsCard
        heading="Active users"
        data={activeUsers}
        startDateValue={activeUsersStart}
        endDateValue={activeUsersEnd}
        isLoading={activeUsersLoading}
        onStartDateChange={(e) => handleActiveUsersStart(e)}
        onEndDateChange={(e) => handleActiveUsersEnd(e)}
        handleFilter={() => handleActiveUsersFilter()}
      />

      <AnalyticsCard
        heading="First open"
        data={firstOpen}
        startDateValue={firstOpenStart}
        endDateValue={firstOpenEnd}
        isLoading={firstOpenLoading}
        onStartDateChange={(e) => handleFirstOpenStart(e)}
        onEndDateChange={(e) => handleFirstOpenEnd(e)}
        handleFilter={() => handleFirstOpenFilter()}
      />
    </div>
  );
};

export default Analytics;